import React, { useState, useEffect, useCallback } from "react";
import { Table, Spin, Select, Modal, Radio, Input, InputNumber, Upload, Tag, Alert } from "antd";
import { PlusOutlined, EditOutlined, EyeOutlined, UploadOutlined, FileOutlined, SaveOutlined, SendOutlined } from "@ant-design/icons";
import DetailsDrawer, { DetailsField, AttachmentPreview } from "../../components/detailsDrawer";
import { getData, checkStatus, parseJSON, getLocationOrigin, defaultOptions } from "../../config/api-connect";
import * as Styled from "./style.js";
import "./styles.css";
import { getSearchFilter, getDropdownFilter } from "../../utils/filterHelper.jsx";
import moment from "moment";
import Spinner from "../../components/spinner";

const { Option } = Select;
const { TextArea } = Input;

const STATUS_META = {
  Draft: { color: "default" },
  Pending: { color: "warning" },
  Approved: { color: "success" },
  Rejected: { color: "error" },
};

const formatAmount = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

async function postFormData(url, formData) {
  const custom_url = `${getLocationOrigin()}${url}`;
  const options = { ...defaultOptions };
  options.method = "POST";
  options.body = formData;
  const response = await fetch(custom_url, options)
    .then(checkStatus)
    .then(parseJSON);
  return response;
}

const Reimbursement = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reimbursements, setReimbursements] = useState([]);
  const [options, setOptions] = useState({
    regions: [],
    venues: [],
    expenseHeads: [],
    expenseGroups: [],
  });
  const [month, setMonth] = useState(
    () => JSON.parse(localStorage.getItem("month")) || moment().format("MM"),
  );
  const [year, setYear] = useState(
    () => JSON.parse(localStorage.getItem("year")) || moment().format("YYYY"),
  );
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const [expenseType, setExpenseType] = useState("venue");
  const [fileList, setFileList] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [viewRecord, setViewRecord] = useState(null);
  const [viewDetail, setViewDetail] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [formData, setFormData] = useState({
    region: "",
    venue_id: "",
    expense_group_id: "",
    expense_head_id: "",
    purpose: "",
    amount: "",
    comments: "",
  });
  const [formErrors, setFormErrors] = useState({});

  const years = [];
  for (let y = moment().year(); y >= 2015; y--) years.push(String(y));

  const loadOptions = useCallback(async () => {
    try {
      const data = await getData("/finance/get_expense_reimbursement_details/");
      setOptions({
        regions: data.regions || [],
        venues: data.venues || [],
        expenseHeads: data.expense_heads || [],
        expenseGroups: data.expense_groups || [],
      });
    } catch (error) {
      console.error("Error fetching reimbursement options:", error);
    }
  }, []);

  const fetchReimbursements = useCallback(async () => {
    setLoading(true);
    try {
      let url = "/finance/list_expense_reimbursements/";
      if (month && year) url += `?month=${month}&year=${year}`;
      const response = await getData(url);
      setReimbursements((response && response.result) || []);
    } catch (error) {
      console.error("Error fetching reimbursements:", error);
    }
    setLoading(false);
  }, [month, year]);

  useEffect(() => {
    localStorage.setItem("month", JSON.stringify(month));
    localStorage.setItem("year", JSON.stringify(year));
    fetchReimbursements();
  }, [month, year, fetchReimbursements]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const openAddModal = () => {
    setEditingDraft(null);
    setExpenseType("venue");
    setFileList([]);
    setExistingAttachments([]);
    setFormData({
      region: "",
      venue_id: "",
      expense_group_id: "",
      expense_head_id: "",
      purpose: "",
      amount: "",
      comments: "",
    });
    setFormErrors({});
    setModalVisible(true);
  };

  const openEditModal = async (record) => {
    setEditingDraft(record);
    setExpenseType("venue");
    setFileList([]);
    setExistingAttachments([]);
    setFormData({
      region: "",
      venue_id: "",
      expense_group_id: "",
      expense_head_id: "",
      purpose: "",
      amount: "",
      comments: "",
    });
    setFormErrors({});
    setModalVisible(true);
    try {
      const data = await getData(
        `/finance/get_expense_reimbursement_detail/?id=${record.id}`,
      );
      const d = data && data.result;
      if (!d) return;
      const isGroup = !!d.expense_group;
      setExpenseType(isGroup ? "group" : "venue");
      setExistingAttachments(d.attachments || []);
      setFormData({
        region: d.region || "",
        venue_id: d.venue || "",
        expense_group_id: d.expense_group || "",
        expense_head_id: d.expense_head || "",
        purpose: d.purpose || "",
        amount:
          d.amount !== undefined && d.amount !== null && d.amount !== ""
            ? String(d.amount)
            : "",
        comments: d.comments || "",
      });
    } catch (error) {
      console.error("Error loading draft:", error);
    }
  };

  const openViewModal = async (record) => {
    setViewRecord(record);
    setViewDetail(null);
    setViewLoading(true);
    try {
      const data = await getData(
        `/finance/get_expense_reimbursement_detail/?id=${record.id}`,
      );
      setViewDetail(data && data.result);
    } catch (error) {
      console.error("Error loading details:", error);
    } finally {
      setViewLoading(false);
    }
  };

  const buildFormData = (saveAsDraft) => {
    const fd = new FormData();
    if (editingDraft) {
      fd.append("id", String(editingDraft.id));
      if (!saveAsDraft) fd.append("submit", "true");
    } else if (saveAsDraft) {
      fd.append("save_as_draft", "true");
    }
    if (formData.region) fd.append("region", String(formData.region));
    if (expenseType === "venue" && formData.venue_id)
      fd.append("venue_id", String(formData.venue_id));
    if (expenseType === "group" && formData.expense_group_id)
      fd.append("expense_group_id", String(formData.expense_group_id));
    if (formData.expense_head_id)
      fd.append("expense_head_id", String(formData.expense_head_id));
    if (formData.purpose && formData.purpose.trim())
      fd.append("purpose", formData.purpose.trim());
    if (formData.amount !== "" && formData.amount !== null && formData.amount !== undefined)
      fd.append("amount", String(formData.amount));
    if (formData.comments && formData.comments.trim())
      fd.append("comments", formData.comments.trim());
    fileList.forEach((f) => {
      if (f.originFileObj) fd.append("attachments", f.originFileObj);
    });
    return fd;
  };

  const saveReimbursement = async (saveAsDraft) => {
    const url = editingDraft
      ? "/finance/update_expense_reimbursement/"
      : "/finance/create_expense_reimbursement/";
    const response = await postFormData(url, buildFormData(saveAsDraft));
    if (!response.success) {
      throw new Error(response.message || "Request failed");
    }
    return response;
  };

  const handleSaveDraft = async () => {
    if (!formData.expense_head_id) {
      setFormErrors({ ...formErrors, expense_head_id: "Expense head is required" });
      return;
    }
    setSaving(true);
    try {
      await saveReimbursement(true);
      setModalVisible(false);
      fetchReimbursements();
    } catch (error) {
      console.error("Error saving draft:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    const errors = {};
    if (!formData.region) errors.region = "Region is required";
    if (expenseType === "venue" && !formData.venue_id)
      errors.venue_id = "Venue is required";
    if (expenseType === "group" && !formData.expense_group_id)
      errors.expense_group_id = "Expense group is required";
    if (!formData.expense_head_id) errors.expense_head_id = "Expense head is required";
    if (!formData.purpose || !formData.purpose.trim())
      errors.purpose = "Purpose is required";
    if (formData.amount === "" || formData.amount === null || formData.amount === undefined)
      errors.amount = "Amount is required";
    else if (Number(formData.amount) <= 0)
      errors.amount = "Amount must be greater than 0";

    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setSaving(true);
    try {
      await saveReimbursement(false);
      setModalVisible(false);
      fetchReimbursements();
    } catch (error) {
      console.error("Error submitting reimbursement:", error);
    } finally {
      setSaving(false);
    }
  };

  const renderHeader = (title) => (
    <div
      style={{ textAlign: "center", fontWeight: "bold", textWrap: "nowrap" }}
    >
      {title}
    </div>
  );

  const columns = [
    {
      title: renderHeader("Requested On"),
      dataIndex: "requested_on",
      key: "requested_on",
      align: "center",
      ...getSearchFilter("requested_on", "Requested On"),
    },
    {
      title: renderHeader("Expense Head"),
      dataIndex: "category",
      key: "category",
      align: "center",
      ...getDropdownFilter("category", reimbursements),
    },
    {
      title: renderHeader("Region"),
      dataIndex: "region_label",
      key: "region_label",
      align: "center",
      ...getDropdownFilter("region_label", reimbursements),
    },
    {
      title: renderHeader("Expense Group / Venue"),
      key: "venue_group",
      align: "center",
      render: (_, r) => r.venue_name || r.expense_group_name || "-",
    },
    {
      title: renderHeader("Amount"),
      key: "amount",
      align: "center",
      render: (_, r) => `\u20B9 ${formatAmount(r.amount)}`,
    },
    {
      title: renderHeader("Status"),
      dataIndex: "status",
      key: "status",
      align: "center",
      ...getDropdownFilter("status", reimbursements),
      render: (s) => {
        const meta = STATUS_META[s] || { color: "default" };
        return <Tag color={meta.color}>{s || "Unknown"}</Tag>;
      },
    },
    {
      title: renderHeader("Purpose"),
      dataIndex: "description",
      key: "description",
      ...getSearchFilter("description", "Purpose"),
    },
    {
      title: renderHeader("Actions"),
      key: "actions",
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          {String(record.status).toLowerCase() === "draft" ? (
            <button
              className="btn btn-sm"
              style={{ background: "#fff", color: "#222", border: "1px solid #ccc", borderRadius: 6, display: "flex", alignItems: "center", padding: "4px 8px", gap: 2 }}
              onClick={() => openEditModal(record)}
            >
              <EditOutlined style={{ marginRight: 2 }} /> Continue
            </button>
          ) : (
            <button
              className="btn btn-sm"
              style={{ background: "#fff", color: "#222", border: "1px solid #ccc", borderRadius: 6, display: "flex", alignItems: "center", padding: "4px 8px", gap: 2 }}
              onClick={() => openViewModal(record)}
            >
              <EyeOutlined style={{ marginRight: 2 }} /> View
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Spin spinning={loading} indicator={<Spinner />}>
      <Styled.MainContainer className="buddyAttendance">
        <Styled.TopDiv>
          <Styled.SelectorContainer>
            <Styled.Selector>
              <span className="indicatorText">Month</span>
              <Select
                className="mainSelector"
                style={{ width: 150 }}
                onChange={setMonth}
                value={month}
                bordered={false}
              >
                <Option value="12"> December </Option>
                <Option value="11"> November </Option>
                <Option value="10"> October </Option>
                <Option value="09"> September </Option>
                <Option value="08"> August </Option>
                <Option value="07"> July </Option>
                <Option value="06"> June </Option>
                <Option value="05"> May </Option>
                <Option value="04"> April </Option>
                <Option value="03"> March </Option>
                <Option value="02"> February </Option>
                <Option value="01"> January </Option>
              </Select>
            </Styled.Selector>

            <Styled.Selector>
              <span className="indicatorText">Year</span>
              <Select
                className="mainSelector"
                style={{ width: 120 }}
                onChange={setYear}
                value={year}
                bordered={false}
              >
                {years.map((y) => (
                  <Option value={y} key={y}>
                    {y}
                  </Option>
                ))}
              </Select>
            </Styled.Selector>
          </Styled.SelectorContainer>
        </Styled.TopDiv>

        <Styled.NewHeader>
          <span></span>
          <span> Expense Reimbursements </span>
          <button className="btn btn-sm downloadBtn" onClick={openAddModal}>
            <PlusOutlined style={{ marginRight: 4 }} /> New Request
          </button>
        </Styled.NewHeader>

        <div className="recordCount">{reimbursements.length || 0} records</div>
        <Table
          className="drill-down-table table_container"
          rowKey="id"
          columns={columns}
          dataSource={reimbursements}
          pagination={{ pageSize: 50, showSizeChanger: false }}
          scroll={{ x: "max-content" }}
        />

        <Modal
          title={editingDraft ? "Continue Reimbursement Request" : "New Reimbursement Request"}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
        >
          <div className="reimbursement-form" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {editingDraft && (
              <Alert
                type="info"
                showIcon
                message="This is a draft. Complete the details and submit."
              />
            )}

            <div>
              <label>Region</label>
              <Select
                style={{ width: "100%" }}
                showSearch
                optionFilterProp="children"
                value={formData.region || undefined}
                placeholder="Select Region"
                onChange={(value) => { setFormData({ ...formData, region: value }); setFormErrors({ ...formErrors, region: "" }); }}
                status={formErrors.region ? "error" : ""}
              >
                {options.regions.map((item) => (
                  <Option value={item.id} key={item.id}>
                    {item.name}
                  </Option>
                ))}
              </Select>
              {formErrors.region && <div style={{ color: "#ff4d4f", fontSize: 12 }}>{formErrors.region}</div>}
            </div>

            <div>
              <label>Venue / Expense Group</label>
              <Radio.Group
                value={expenseType}
                onChange={(e) => {
                  setExpenseType(e.target.value);
                  setFormData({ ...formData, venue_id: "", expense_group_id: "" });
                  setFormErrors({ ...formErrors, venue_id: "", expense_group_id: "" });
                }}
                style={{display: 'flex'}}
              >
                <Radio.Button value="venue">Venue</Radio.Button>
                <Radio.Button value="group">Expense Group</Radio.Button>
              </Radio.Group>
              <Select
                style={{ width: "100%", marginTop: 8 }}
                showSearch
                optionFilterProp="children"
                value={expenseType === "venue" ? formData.venue_id || undefined : formData.expense_group_id || undefined}
                placeholder={expenseType === "venue" ? "Search venue..." : "Select Expense Group"}
                onChange={(value) => {
                  if (expenseType === "venue") {
                    setFormData({ ...formData, venue_id: value });
                    setFormErrors({ ...formErrors, venue_id: "" });
                  } else {
                    setFormData({ ...formData, expense_group_id: value });
                    setFormErrors({ ...formErrors, expense_group_id: "" });
                  }
                }}
                status={formErrors.venue_id || formErrors.expense_group_id ? "error" : ""}
              >
                {(expenseType === "venue" ? options.venues : options.expenseGroups).map((item) => (
                  <Option value={item.id} key={item.id}>
                    {item.name}
                  </Option>
                ))}
              </Select>
              {(formErrors.venue_id || formErrors.expense_group_id) && (
                <div style={{ color: "#ff4d4f", fontSize: 12 }}>
                  {formErrors.venue_id || formErrors.expense_group_id}
                </div>
              )}
            </div>

            <div>
              <label>Expense Head</label>
              <Select
                style={{ width: "100%" }}
                showSearch
                optionFilterProp="children"
                value={formData.expense_head_id || undefined}
                placeholder="Select Expense Head"
                onChange={(value) => { setFormData({ ...formData, expense_head_id: value }); setFormErrors({ ...formErrors, expense_head_id: "" }); }}
                status={formErrors.expense_head_id ? "error" : ""}
              >
                {options.expenseHeads.map((item) => (
                  <Option value={item.id} key={item.id}>
                    {item.name}
                  </Option>
                ))}
              </Select>
              {formErrors.expense_head_id && <div style={{ color: "#ff4d4f", fontSize: 12 }}>{formErrors.expense_head_id}</div>}
            </div>

            <div>
              <label>Purpose</label>
              <TextArea
                rows={3}
                value={formData.purpose}
                onChange={(e) => { setFormData({ ...formData, purpose: e.target.value }); setFormErrors({ ...formErrors, purpose: "" }); }}
                placeholder="Describe the purpose of this expense..."
              />
              {formErrors.purpose && <div style={{ color: "#ff4d4f", fontSize: 12 }}>{formErrors.purpose}</div>}
            </div>

            <div>
              <label>Amount</label>
              <InputNumber
                style={{ width: "100%" }}
                min={0.01}
                value={formData.amount || undefined}
                placeholder="Enter amount"
                onChange={(value) => { setFormData({ ...formData, amount: value }); setFormErrors({ ...formErrors, amount: "" }); }}
                status={formErrors.amount ? "error" : ""}
              />
              {formErrors.amount && <div style={{ color: "#ff4d4f", fontSize: 12 }}>{formErrors.amount}</div>}
            </div>

            <div>
              <label>Comments</label>
              <TextArea
                rows={2}
                value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                placeholder="Any additional comments (optional)"
              />
            </div>

            <div>
              <label>Attachments</label>
              <Upload
                multiple
                beforeUpload={() => false}
                fileList={fileList}
                onChange={({ fileList: fl }) => setFileList(fl)}
              >
                <button
                  className="btn btn-sm"
                  style={{ background: "#fff", color: "#222", border: "1px solid #ccc", borderRadius: 6, display: "flex", alignItems: "center", padding: "4px 8px", gap: 2 }}
                >
                  <UploadOutlined style={{ marginRight: 2 }} /> Upload
                </button>
              </Upload>
              {existingAttachments.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 12, color: "grey" }}>Existing attachments:</div>
                  {existingAttachments.map((a) => (
                    <div key={a.id}>
                      <a href={a.file_url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                        <FileOutlined /> {a.file_name}
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                className="btn btn-sm"
                style={{ background: "#fff", color: "#222", border: "1px solid #ccc", borderRadius: 6, display: "flex", alignItems: "center", padding: "4px 8px", gap: 2 }}
                onClick={() => setModalVisible(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm"
                style={{ background: "#fff", color: "#02475c", border: "1px solid #02475c", borderRadius: 6, display: "flex", alignItems: "center", padding: "4px 8px", gap: 2 }}
                onClick={handleSaveDraft}
                disabled={saving}
              >
                <SaveOutlined style={{ marginRight: 2 }} /> Save As Draft
              </button>
              <button
                className="btn btn-sm downloadBtn"
                onClick={handleSubmit}
                disabled={saving}
              >
                <SendOutlined style={{ marginRight: 2 }} /> Submit Request
              </button>
            </div>
          </div>
        </Modal>

        <DetailsDrawer
          open={!!viewRecord}
          onClose={() => setViewRecord(null)}
          title="Reimbursement Details"
        >
          <Spin spinning={viewLoading}>
            {viewDetail && (
              <>
                <DetailsField label="Requested On" value={viewDetail.requested_on} />
                <DetailsField label="Expense Head" value={viewDetail.category} />
                <DetailsField label="Region" value={viewDetail.region_label} />
                <DetailsField
                  label="Expense Group / Venue"
                  value={viewDetail.venue_name || viewDetail.expense_group_name}
                />
                <DetailsField
                  label="Amount"
                  value={`\u20B9 ${formatAmount(viewDetail.amount)}`}
                />
                <DetailsField
                  label="Status"
                  value={
                    <Tag color={(STATUS_META[viewDetail.status] || {}).color}>
                      {viewDetail.status}
                    </Tag>
                  }
                />
                <DetailsField
                  label="Purpose"
                  value={viewDetail.purpose || viewDetail.description}
                />
                <DetailsField label="Comments" value={viewDetail.comments} />
                <DetailsField
                  label="Attachments"
                  value={<AttachmentPreview attachments={viewDetail.attachments} />}
                />
              </>
            )}
          </Spin>
        </DetailsDrawer>
      </Styled.MainContainer>
    </Spin>
  );
};

export default Reimbursement;
